import * as Sentry from '@sentry/react';
import axios from 'axios';

enum SentryErrorName {
  CanceledError = 'CanceledError',
}

const ERROR_BOUNDARY_MECHANISM = 'auto.function.react.error_boundary';

// The plan's replay quota is tiny, so only sessions that ended on the global crash screen are recorded.
export const shouldCaptureReplay = (event: Sentry.ErrorEvent): boolean =>
  event.exception?.values?.some(exception => exception.mechanism?.type === ERROR_BOUNDARY_MECHANISM) ?? false;

export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_APP_ENV;

  if (environment === 'development') {
    console.warn('Skipping Sentry initialization in development environment');
    return;
  }
  if (!dsn) {
    console.warn('Sentry DSN not configured. Error tracking will be disabled.');
    return;
  }

  const excludedErrorNames = [SentryErrorName.CanceledError];

  Sentry.init({
    dsn,
    environment,
    denyUrls: [/heap-api\.com/, /zdassets\.com/, /zendesk\.com/],
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
        beforeErrorSampling: shouldCaptureReplay,
      }),
    ],
    tracesSampleRate: 0.5,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    profilesSampleRate: 0.1,
    beforeSend(event, hint) {
      const error = hint?.originalException;
      if (error instanceof Error && excludedErrorNames.includes(error.name as SentryErrorName)) {
        return null;
      }
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        if (status && status >= 400 && status < 500) {
          return null;
        }
      }
      return event;
    },
  });
};
